import { api, throwApiError } from "../../api/api";

export type EvaluationQuestionType =
  | "BOOLEAN"
  | "SCALE"
  | "TEXT"
  | "DATE"
  | "SINGLE_CHOICE"
  | "MULTI_CHOICE"
  | "IMAGE_UPLOAD"
  | string;

export type EvaluationAnswererType =
  | "ADMIN"
  | "SUPERVISOR"
  | "SUPERVISOR_DE_CAMPO"
  | "FREELANCER";

export type CreateEvaluationFormOptionRequest = {
  label: string;
  value?: string;
  order: number;
};

export type CreateEvaluationFormQuestionRequest = {
  type: EvaluationQuestionType;
  prompt: string;
  required?: boolean;
  order: number;
  dimension?: string;
  minValue?: number;
  maxValue?: number;
  options?: CreateEvaluationFormOptionRequest[];
};

export type CreateEvaluationFormRequest = {
  name: string; // Backend CreateEvaluationFormDto uses 'name' (maps to title)
  category: "LOJA" | "FREELANCER" | string;
  allowedAnswererTypes: EvaluationAnswererType[];
  questions?: CreateEvaluationFormQuestionRequest[];
};

export type UpdateEvaluationFormRequest = {
  category?: "LOJA" | "FREELANCER" | string;
  title?: string;
  allowedAnswererTypes?: EvaluationAnswererType[];
  archivedAt?: string | null;
};

export type ListEvaluationFormsQuery = {
  title?: string;
  category?: "LOJA" | "FREELANCER" | string;
  includeArchived?: boolean;
  includeQuestions?: boolean;
  answererType?: EvaluationAnswererType;
};

export type EvaluationQuestionOption = {
  id: string;
  questionId: string;
  label: string;
  value?: string | null;
  order: number;
};

export type EvaluationQuestion = {
  id: string;
  formId: string;
  type: EvaluationQuestionType;
  prompt: string;
  required: boolean;
  order: number;
  weight: number;
  dimension?: string | null;
  minValue?: number | null;
  maxValue?: number | null;
  archivedAt?: string | null;
  options?: EvaluationQuestionOption[];
  createdAt: string;
  updatedAt: string;
};

export type EvaluationForm = {
  id: string;
  formTypeId: string;
  category: string;
  title: string;
  allowedAnswererTypes: EvaluationAnswererType[];
  archivedAt?: string | null;
  questions?: EvaluationQuestion[];
  createdAt: string;
  updatedAt: string;
};

export const createEvaluationFormApi = async (
  dto: CreateEvaluationFormRequest
): Promise<EvaluationForm> => {
  try {
    const { data } = await api.post<EvaluationForm>("/evaluation-forms", dto);
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao criar formulário de avaliação.");
    throw error;
  }
};

export const listEvaluationFormsApi = async (
  query: ListEvaluationFormsQuery = {}
): Promise<EvaluationForm[]> => {
  try {
    const { data } = await api.get<EvaluationForm[]>("/evaluation-forms", { params: query });
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao listar formulários de avaliação.");
    throw error;
  }
};

export const getEvaluationFormByIdApi = async (formId: string): Promise<EvaluationForm> => {
  try {
    const { data } = await api.get<EvaluationForm>(`/evaluation-forms/${formId}`);
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao buscar formulário de avaliação.");
    throw error;
  }
};

export const updateEvaluationFormApi = async (
  formId: string,
  dto: UpdateEvaluationFormRequest
): Promise<EvaluationForm> => {
  try {
    const { data } = await api.patch<EvaluationForm>(`/evaluation-forms/${formId}`, dto);
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao atualizar formulário de avaliação.");
    throw error;
  }
};

export const archiveEvaluationFormApi = async (formId: string): Promise<EvaluationForm> => {
  try {
    const { data } = await api.delete<EvaluationForm>(`/evaluation-forms/${formId}`);
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao arquivar formulário de avaliação.");
    throw error;
  }
};
